import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MagicWand, Warning, CheckCircle, MagnifyingGlass, CircleNotch, CaretRight } from "@phosphor-icons/react";

/**
 * Party-wise OCR re-check (v1.20).
 *
 *  - Top row shows the primary "Start re-check" button + a secondary
 *    "Re-check all" fallback and the live progress bar.
 *  - Clicking "Start re-check" opens a party picker (search + sorted list
 *    with mismatches first, then unchecked, then clean — alphabetical
 *    within each group).
 *  - Selecting a party kicks off a party-scoped background recheck.
 *  - Each party row is clickable → filters the Payment Slips table to
 *    that party (via `onSelectParty` prop) so mismatches can be fixed
 *    immediately.
 *
 * Slips already flagged as `ocr_mismatch=true` are skipped on subsequent
 * rechecks (backend contract) — admin already sees them red; the OCR
 * quota is spent on unchecked slips instead.
 */
export default function ReverifyPanel({ onSelectParty }) {
    const [progress, setProgress] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [parties, setParties] = useState([]);
    const [loadingParties, setLoadingParties] = useState(false);
    const [search, setSearch] = useState("");
    const pollRef = useRef(null);

    const fetchStatus = async () => {
        try { const { data } = await api.get("/admin/uploads/reverify-status"); setProgress(data); }
        catch { /* silent */ }
    };
    const fetchParties = async () => {
        setLoadingParties(true);
        try {
            const { data } = await api.get("/admin/uploads/reverify-parties");
            setParties(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoadingParties(false); }
    };
    useEffect(() => { fetchStatus(); fetchParties(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

    // Poll every 2s while a recheck is running; refresh party summary once it finishes.
    useEffect(() => {
        if (progress?.running) {
            pollRef.current = setInterval(fetchStatus, 2000);
        } else if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // Job just finished → refresh party status
            fetchParties();
        }
    }, [progress?.running]); // eslint-disable-line react-hooks/exhaustive-deps

    const startForParty = async (party) => {
        if (progress?.running) {
            toast.error("A re-check is already running — wait for it to finish");
            return;
        }
        if (!window.confirm(`Start OCR re-check for "${party.party_name}"?\n\nThis will scan ${party.total_verified - party.mismatch_count} slip(s) (already-flagged slips are skipped). Runs in the background.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/uploads/reverify-party/${party.party_id}?only_verified=true`);
            setProgress(data);
            setPickerOpen(false);
            toast.success(data.already_running
                ? "A re-check is already in progress"
                : `Re-checking "${party.party_name}" — you'll see red rows appear as mismatches are found`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const startAll = async () => {
        if (!window.confirm("Re-check ALL verified slips across every party? This may take a while.\n\nAlready-flagged slips will be skipped.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/uploads/reverify-all?only_verified=true");
            setProgress(data);
            toast.success(data.already_running ? "Re-check already in progress" : "Full re-check started");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return parties;
        return parties.filter((p) => (p.party_name || "").toLowerCase().includes(q));
    }, [parties, search]);

    const pct = progress?.total ? Math.round((progress.done / progress.total) * 100) : 0;
    const summary = useMemo(() => {
        const errors = parties.filter((p) => p.status === "errors").length;
        const clean = parties.filter((p) => p.status === "clean").length;
        const unchecked = parties.filter((p) => p.status === "unchecked").length;
        return { errors, clean, unchecked };
    }, [parties]);

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="reverify-panel">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <MagicWand size={13} weight="fill" className="text-blue-600" />
                        <div className="font-display text-sm font-semibold text-slate-900">OCR Re-check — party by party</div>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-600">
                        Pick a party, scan its slips, and fix any where saved amount / UTR / date disagrees with OCR. Flagged rows turn <span className="rounded bg-rose-100 px-1 py-0.5 font-semibold text-rose-800">⚠ OCR mismatch red</span> until you edit + save the correction. Already-flagged slips are skipped on re-runs.
                    </div>
                    {/* Party status summary */}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="reverify-party-summary">
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-800" data-testid="reverify-summary-errors">
                            <Warning size={10} weight="fill" /> {summary.errors} with errors
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700" data-testid="reverify-summary-unchecked">
                            {summary.unchecked} unchecked
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800" data-testid="reverify-summary-clean">
                            <CheckCircle size={10} weight="fill" /> {summary.clean} clean
                        </span>
                    </div>
                    {/* Live progress bar */}
                    {progress && (progress.running || progress.done > 0) && (
                        <div className="mt-3 flex items-center gap-3" data-testid="reverify-progress">
                            <div className="h-1.5 flex-1 max-w-md overflow-hidden rounded-full bg-slate-200">
                                <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-mono text-slate-700">
                                {progress.party_name && (
                                    <span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-800" data-testid="reverify-progress-party">
                                        {progress.party_name}
                                    </span>
                                )}
                                {progress.done}/{progress.total}
                                {progress.flagged > 0 && (
                                    <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-800" data-testid="reverify-flagged">
                                        <Warning size={10} weight="fill" className="inline align-middle" /> {progress.flagged} flagged
                                    </span>
                                )}
                                {progress.running ? <span className="ml-2 italic text-blue-600">running…</span> : progress.finished_at ? <span className="ml-2 italic text-emerald-700">complete</span> : null}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-end gap-2">
                    <Button
                        onClick={() => { setSearch(""); setPickerOpen(true); fetchParties(); }}
                        disabled={busy || progress?.running}
                        data-testid="reverify-start"
                        className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        <MagicWand size={14} weight="fill" className="mr-1.5" />
                        {progress?.running ? "Re-checking…" : "Start Re-check"}
                    </Button>
                    <button
                        type="button"
                        onClick={startAll}
                        disabled={busy || progress?.running}
                        data-testid="reverify-start-all"
                        className="text-[11px] font-medium text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:opacity-40"
                    >
                        or re-check all parties at once
                    </button>
                </div>
            </div>

            {/* Party list (always visible below the header, so admin sees status at a glance) */}
            <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-slate-100" data-testid="reverify-party-list">
                {loadingParties && parties.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-500">Loading parties…</div>
                ) : parties.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-500">No active parties.</div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {parties.map((p) => (
                            <PartyRow
                                key={p.party_id}
                                party={p}
                                busy={busy || Boolean(progress?.running)}
                                onSelectParty={onSelectParty}
                                onStart={() => startForParty(p)}
                                isRunning={progress?.running && progress?.party_id === p.party_id}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {/* Picker dialog — search + pick a party */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="flex max-h-[80vh] max-w-lg flex-col p-0" data-testid="reverify-picker">
                    <DialogHeader className="border-b border-slate-200 px-5 py-3">
                        <DialogTitle className="font-display text-base">Pick a party to re-check</DialogTitle>
                    </DialogHeader>
                    <div className="border-b border-slate-100 px-5 py-3">
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                autoFocus
                                data-testid="reverify-picker-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search party by name…"
                                className="pl-8 h-9 text-sm"
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2 py-1" data-testid="reverify-picker-list">
                        {filtered.length === 0 ? (
                            <div className="p-6 text-center text-sm text-slate-500">No parties match "{search}".</div>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {filtered.map((p) => (
                                    <PartyRow
                                        key={p.party_id}
                                        party={p}
                                        busy={busy || Boolean(progress?.running)}
                                        onSelectParty={null}
                                        onStart={() => startForParty(p)}
                                        isRunning={progress?.running && progress?.party_id === p.party_id}
                                        compact
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function statusPill(status, mismatch, total) {
    if (status === "errors") return { bg: "bg-rose-100", text: "text-rose-800", label: `${mismatch} error${mismatch === 1 ? "" : "s"}`, dot: "bg-rose-500" };
    if (status === "clean") return { bg: "bg-emerald-100", text: "text-emerald-800", label: total === 0 ? "no slips" : "clean", dot: "bg-emerald-500" };
    return { bg: "bg-slate-100", text: "text-slate-700", label: total === 0 ? "no slips" : "unchecked", dot: "bg-slate-400" };
}

function PartyRow({ party, busy, onSelectParty, onStart, isRunning, compact }) {
    const p = statusPill(party.status, party.mismatch_count, party.total_verified);
    const nameColor = party.status === "errors" ? "text-rose-700" : party.status === "clean" ? "text-emerald-700" : "text-slate-700";
    const clickable = Boolean(onSelectParty);
    return (
        <li className={`flex items-center gap-2 px-3 ${compact ? "py-1.5" : "py-2"} transition-colors hover:bg-slate-50`} data-testid={`reverify-row-${party.party_id}`} data-status={party.status}>
            <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${p.dot}`} aria-hidden />
            <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onSelectParty(party.party_id)}
                data-testid={`reverify-row-name-${party.party_id}`}
                className={`min-w-0 flex-1 truncate text-left text-sm font-semibold ${nameColor} ${clickable ? "hover:underline hover:decoration-dotted underline-offset-2" : ""} disabled:cursor-default`}
                title={clickable ? "Open this party in the Payment Slips list" : party.party_name}
            >
                {party.party_name}
                {clickable && party.status === "errors" && <CaretRight size={10} weight="bold" className="ml-1 inline align-middle text-rose-500" />}
            </button>
            <span className="hidden text-[10px] text-slate-500 sm:inline" data-testid={`reverify-row-counts-${party.party_id}`}>
                {party.total_verified} slip{party.total_verified === 1 ? "" : "s"}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.bg} ${p.text}`} data-testid={`reverify-row-status-${party.party_id}`}>
                {p.label}
            </span>
            <button
                type="button"
                onClick={onStart}
                disabled={busy || party.total_verified === 0}
                data-testid={`reverify-row-start-${party.party_id}`}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-[11px] font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
                {isRunning ? (<><CircleNotch size={12} className="animate-spin" /> Running</>) : "Re-check"}
            </button>
        </li>
    );
}
